import Vue from "vue";
import Vuex from "vuex";
import VuexPersist from "vuex-persist";

import shopifyTasks from "@/store/modules/shopifyTasks";
import tasks from "@/store/modules/tasks";
import app from "@/store/modules/app";
import user from "@/store/modules/user";
import users from "@/store/modules/users";
import settings from "@/store/modules/settings";
import orders from "@/store/modules/orders";
import manualFulfillments from "@/store/modules/manualFulfillments";
import statistics from "@/store/modules/statistics";
import support from "@/store/modules/support";
import sendQueue from "@/store/modules/sendQueue";
import counters from "@/store/modules/counters";
import localforage from "localforage";

Vue.use(Vuex);

// Persistenz-Cache (app/users/user/settings) in IndexedDB.
// 21.10.2026: vuex-persist lief bisher bei JEDER Mutation des ganzen Stores
// (auch 400× Ticket-Updates beim Start) und serialisierte dabei synchron den
// kompletten Cache — mit Maltes 2,5-MB-Nutzerdokument (unpaidTasks) fror die
// Oberfläche 5–10 s ein. Jetzt: nur Mutationen der gecachten Module zählen,
// unpaidTasks werden nicht gecacht (der Live-Listener liefert sie sofort nach),
// und geschrieben wird gebündelt höchstens einmal pro Sekunde.
const PERSISTED_MODULES = { app, users, user, settings };
const persistedMutations = new Set(
  Object.values(PERSISTED_MODULES).flatMap((m) => Object.keys(m.mutations || {}))
);

const withoutUnpaidTasks = (u) =>
  u && u.payment && Array.isArray(u.payment.unpaidTasks)
    ? { ...u, payment: { ...u.payment, unpaidTasks: [] } }
    : u;

let pendingWrite = null;
let writeTimer = null;
const flushPersist = (storage, key) => {
  writeTimer = null;
  if (pendingWrite === null) return;
  const data = pendingWrite;
  pendingWrite = null;
  return storage.setItem(key, data).catch((e) => console.warn("Persist fehlgeschlagen:", e));
};

const vuexPersist = new VuexPersist({
  key: "taskDash",
  storage: localforage,
  asyncStorage: true,
  filter: (mutation) => persistedMutations.has(mutation.type),
  reducer: (state) => {
    // Der "Waschgang":
    // 1. JSON.stringify entfernt alle Funktionen (macht es "sauber" aber zu Text)
    // 2. JSON.parse macht daraus wieder ein Objekt
    // Ergebnis: Ein sauberes Objekt ohne Funktionen, das localforage akzeptiert.
    return JSON.parse(
      JSON.stringify({
        app: state.app,
        users: { ...state.users, users: (state.users.users || []).map(withoutUnpaidTasks) },
        user: { ...state.user, currentUser: withoutUnpaidTasks(state.user.currentUser) },
        settings: state.settings,
      })
    );
  },
  saveState: (key, state, storage) => {
    pendingWrite = state;
    if (!writeTimer) writeTimer = setTimeout(() => flushPersist(storage, key), 1000);
    return Promise.resolve();
  },
});

// Beim Verlassen der Seite den letzten Stand noch wegschreiben
window.addEventListener("pagehide", () => {
  if (writeTimer) clearTimeout(writeTimer);
  flushPersist(localforage, "taskDash");
});

export default new Vuex.Store({
  modules: {
    app,
    shopifyTasks,
    tasks,
    user,
    users,
    settings,
    orders,
    manualFulfillments,
    statistics,
    support,
    sendQueue,
    counters,
  },
  plugins: [vuexPersist.plugin],
});
